import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Mail, Phone, Linkedin, Globe, Pencil, Trash2, History, StickyNote, User, Handshake } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { NET_COLLECTIONS, NET_STATUSES, NET_EXTRA_FIELDS, SCORE_FIELDS_DECIDEURS } from './collections';
import NetworkCrmTab from './NetworkCrmTab';

const Row = ({ label, value, href }) => value ? (
    <div className="flex gap-3 py-1.5 border-b border-border/50 last:border-0">
        <div className="w-40 shrink-0 text-xs text-foreground/50 pt-0.5">{label}</div>
        <div className="text-sm min-w-0 break-words">
            {href ? <a href={href} target="_blank" rel="noreferrer" className="text-primary hover:underline">{value}</a> : value}
        </div>
    </div>
) : null;

export default function NetworkContactSheet({ contactId, onClose, onEdit, onDeleted, api }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notesDraft, setNotesDraft] = useState('');
    const [savingNotes, setSavingNotes] = useState(false);
    const [activeTab, setActiveTab] = useState('infos');
    const [assignees, setAssignees] = useState([]);

    useEffect(() => {
        api.get('/network/assignees').then(r => setAssignees(r.data.items || [])).catch(() => {});
    }, [api]);

    const load = useCallback(async () => {
        if (!contactId) return;
        setLoading(true);
        try {
            const r = await api.get(`/network/contacts/${contactId}`);
            setData(r.data);
            setNotesDraft(r.data.contact.notes || '');
        } catch {
            toast.error('Contact introuvable');
            onClose();
        } finally {
            setLoading(false);
        }
    }, [contactId, api, onClose]);

    useEffect(() => { load(); }, [load]);

    const c = data?.contact;
    const meta = c ? NET_COLLECTIONS[c.collection] : null;
    const status = c ? NET_STATUSES[c.status_relation] : null;

    const saveNotes = async () => {
        setSavingNotes(true);
        try {
            await api.put(`/network/contacts/${c.id}`, { notes: notesDraft });
            toast.success('Notes enregistrées');
            load();
        } catch {
            toast.error('Échec enregistrement notes');
        } finally {
            setSavingNotes(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Supprimer définitivement « ${c.full_name} » du réseau ?`)) return;
        try {
            await api.delete(`/network/contacts/${c.id}`);
            toast.success('Contact supprimé');
            onDeleted();
            onClose();
        } catch {
            toast.error('Échec de la suppression');
        }
    };

    return (
        <Sheet open={!!contactId} onOpenChange={v => !v && onClose()}>
            <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="network-contact-sheet">
                {loading || !c ? (
                    <>
                        <SheetTitle className="sr-only">Chargement du contact</SheetTitle>
                        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>
                    </>
                ) : (
                    <>
                        <SheetHeader className="space-y-3">
                            <div className="flex items-start gap-3">
                                <div className={`h-12 w-12 rounded-xl border flex items-center justify-center shrink-0 ${meta?.color || ''}`}>
                                    {meta ? <meta.icon size={20} strokeWidth={1.7} /> : <User size={20} />}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <SheetTitle className="font-heading text-2xl leading-tight" data-testid="network-sheet-name">
                                        {c.civility ? `${c.civility} ` : ''}{c.full_name}
                                    </SheetTitle>
                                    <div className="text-sm text-foreground/60 truncate">
                                        {[c.role_function, c.organization].filter(Boolean).join(' · ')}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                        <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${meta?.color || ''}`}>{meta?.label}</span>
                                        {status && <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${status.color}`}>{status.label}</span>}
                                        {c.priority && <span className="px-2 py-0.5 rounded-full border border-border text-[11px]">{c.priority}</span>}
                                        {c.score_total != null && <span className="px-2 py-0.5 rounded-full bg-primary-light text-primary text-[11px] font-semibold">Score {Math.round(c.score_total)}</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {c.email && (
                                    <a href={`mailto:${c.email}`} data-testid="network-sheet-mailto">
                                        <Button size="sm" variant="outline"><Mail size={13} className="mr-1.5" /> Email</Button>
                                    </a>
                                )}
                                {c.phone && (
                                    <a href={`tel:${c.phone}`}>
                                        <Button size="sm" variant="outline"><Phone size={13} className="mr-1.5" /> Appeler</Button>
                                    </a>
                                )}
                                {c.linkedin && (
                                    <a href={c.linkedin.startsWith('http') ? c.linkedin : `https://${c.linkedin}`} target="_blank" rel="noreferrer">
                                        <Button size="sm" variant="outline"><Linkedin size={13} className="mr-1.5" /> LinkedIn</Button>
                                    </a>
                                )}
                                <Button size="sm" variant="outline" onClick={() => onEdit(c)} data-testid="network-sheet-edit">
                                    <Pencil size={13} className="mr-1.5" /> Modifier
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={remove} data-testid="network-sheet-delete">
                                    <Trash2 size={13} className="mr-1.5" /> Supprimer
                                </Button>
                            </div>
                        </SheetHeader>

                        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
                            <TabsList className="w-full bg-surface-muted">
                                <TabsTrigger value="infos" className="flex-1" data-testid="network-sheet-tab-infos"><User size={13} className="mr-1.5" /> Infos</TabsTrigger>
                                <TabsTrigger value="crm" className="flex-1" data-testid="network-sheet-tab-crm"><Handshake size={13} className="mr-1.5" /> CRM</TabsTrigger>
                                <TabsTrigger value="history" className="flex-1" data-testid="network-sheet-tab-history"><History size={13} className="mr-1.5" /> Historique</TabsTrigger>
                                <TabsTrigger value="notes" className="flex-1" data-testid="network-sheet-tab-notes"><StickyNote size={13} className="mr-1.5" /> Notes</TabsTrigger>
                            </TabsList>

                            <TabsContent value="crm" className="mt-4">
                                <NetworkCrmTab
                                    contact={c}
                                    interactions={data.interactions || []}
                                    tasks={data.tasks || []}
                                    engagement={data.engagement}
                                    assignees={assignees}
                                    api={api}
                                    reload={load}
                                />
                            </TabsContent>

                            <TabsContent value="infos" className="mt-4 space-y-4">
                                <div>
                                    <Row label="Email" value={c.email} href={c.email ? `mailto:${c.email}` : null} />
                                    <Row label="Email secondaire" value={c.email_secondary} href={c.email_secondary ? `mailto:${c.email_secondary}` : null} />
                                    <Row label="Téléphone" value={c.phone} />
                                    <Row label="Téléphone 2" value={c.phone_secondary} />
                                    <Row label="Pays" value={[c.city, c.country].filter(Boolean).join(', ')} />
                                    <Row label="Continent" value={c.continent} />
                                    <Row label="Site web" value={c.website} href={c.website ? (c.website.startsWith('http') ? c.website : `https://${c.website}`) : null} />
                                    <Row label="Source" value={c.source} />
                                    <Row label="Réf. import" value={c.ref_id} />
                                    <Row label="Tags" value={(c.tags || []).join(' · ') || null} />
                                </div>

                                {(NET_EXTRA_FIELDS[c.collection] || []).some(([k]) => c.extra?.[k]) && (
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-1">Spécifique {meta?.label}</div>
                                        {(NET_EXTRA_FIELDS[c.collection] || []).map(([k, label]) => (
                                            <Row key={k} label={label} value={c.extra?.[k]} />
                                        ))}
                                    </div>
                                )}

                                {c.collection === 'decideurs' && SCORE_FIELDS_DECIDEURS.some(([k]) => c.extra?.[k] != null) && (
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-2">Scores détaillés</div>
                                        <div className="grid grid-cols-3 gap-2">
                                            {SCORE_FIELDS_DECIDEURS.map(([k, label]) => c.extra?.[k] != null && (
                                                <div key={k} className="rounded-lg border border-border p-2 text-center">
                                                    <div className="font-heading text-lg">{c.extra[k]}</div>
                                                    <div className="text-[10px] text-foreground/50 leading-tight">{label}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="history" className="mt-4">
                                {(data.history || []).length === 0 ? (
                                    <div className="text-center py-8 text-sm text-foreground/50">Aucune modification enregistrée</div>
                                ) : (
                                    <div className="space-y-3">
                                        {data.history.map(h => (
                                            <div key={h.id} className="rounded-lg border border-border p-3" data-testid="network-audit-entry">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-medium capitalize">{{ created: 'Création', updated: 'Modification', deleted: 'Suppression', bulk_updated: 'Édition en masse', imported: 'Import' }[h.action] || h.action}</span>
                                                    <span className="text-[11px] text-foreground/50">{new Date(h.created_at).toLocaleString('fr-FR')}</span>
                                                </div>
                                                <div className="text-[11px] text-foreground/50 mt-0.5">{h.actor_email || '—'}</div>
                                                {h.changes && Object.keys(h.changes).length > 0 && (
                                                    <div className="mt-2 space-y-0.5">
                                                        {Object.entries(h.changes).slice(0, 8).map(([k, v]) => (
                                                            <div key={k} className="text-xs">
                                                                <span className="text-foreground/60">{k}</span> :{' '}
                                                                {v && typeof v === 'object' && 'apres' in v
                                                                    ? <><span className="line-through text-foreground/40">{String(v.avant ?? '∅')}</span> → <span className="font-medium">{String(v.apres ?? '∅')}</span></>
                                                                    : <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="notes" className="mt-4 space-y-3">
                                <Textarea rows={8} value={notesDraft} onChange={e => setNotesDraft(e.target.value)}
                                          placeholder="Notes stratégiques, contexte de la relation, points d'attention…"
                                          data-testid="network-notes-textarea" />
                                <Button onClick={saveNotes} disabled={savingNotes} data-testid="network-notes-save">
                                    {savingNotes && <Loader2 size={14} className="mr-2 animate-spin" />} Enregistrer les notes
                                </Button>
                            </TabsContent>
                        </Tabs>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}
